/**
 * 素材卡片组件
 * 显示素材信息、进度和操作按钮
 */
import React, { useState } from 'react';
import { ShadowMaterial, RetellingRecord } from '../../../db/shadowDb';
import { formatFullTime } from '../services/subtitleParser';

interface MaterialCardProps {
  material: ShadowMaterial;
  onPractice?: (id: string) => void;
  onRetelling?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export function MaterialCard({
  material,
  onPractice,
  onRetelling,
  onDelete,
}: MaterialCardProps) {
  const { id, title, subtitles, durationMs, progress, retellingHistory } = material;
  const [showHistory, setShowHistory] = useState(false);

  const totalSentences = subtitles.length;
  const practicedCount = progress.practicedSentences.length;
  const progressPercent = totalSentences > 0 ? (practicedCount / totalSentences) * 100 : 0;
  const fluency = progress.overallFluency;
  const retellingCount = retellingHistory.length;

  // 获取最新的 Retelling 分数
  const latestRetelling = retellingHistory[0];
  const latestCoverage = latestRetelling?.analysis.coverageScore;

  return (
    <div className="shadow-material-card">
      <div className="material-card-header">
        <span className="material-icon">📹</span>
        <h4 className="material-title">{title}</h4>
      </div>

      <div className="material-meta">
        <span className="meta-item">{formatFullTime(durationMs)}</span>
        <span className="meta-divider">·</span>
        <span className="meta-item">{totalSentences} 句</span>
        {fluency !== undefined && (
          <>
            <span className="meta-divider">·</span>
            <span className="meta-item">流利度 {fluency.toFixed(1)}</span>
          </>
        )}
      </div>

      {/* 进度条 */}
      <div className="material-progress">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <span className="progress-text">
          {practicedCount}/{totalSentences}
        </span>
      </div>

      {/* 操作按钮 */}
      <div className="material-actions">
        <button
          type="button"
          className="action-btn primary"
          onClick={() => onPractice?.(id)}
        >
          {practicedCount > 0 ? '继续练习' : '开始练习'}
        </button>
        <button
          type="button"
          className="action-btn"
          onClick={() => onRetelling?.(id)}
        >
          Retelling
          {retellingCount > 0 && (
            <span className="badge">{retellingCount}</span>
          )}
        </button>
        {onDelete && (
          <button
            type="button"
            className="action-btn danger"
            onClick={() => onDelete(id)}
            title="删除素材"
          >
            ✕
          </button>
        )}
      </div>

      {/* Retelling 最新分数 + 历史入口 */}
      {retellingCount > 0 && (
        <div className="material-retelling-section">
          <button
            type="button"
            className="retelling-history-toggle"
            onClick={() => setShowHistory(!showHistory)}
          >
            <span className="score-label">Retelling 历史:</span>
            <span className={`score-value ${latestCoverage !== undefined && latestCoverage >= 80 ? 'good' : latestCoverage !== undefined && latestCoverage >= 60 ? 'fair' : 'weak'}`}>
              {latestCoverage !== undefined ? `${latestCoverage}%` : '--'}
            </span>
            <span className="history-count">({retellingCount}次)</span>
            <span className="toggle-icon">{showHistory ? '▼' : '▶'}</span>
          </button>

          {/* 历史记录列表 */}
          {showHistory && (
            <div className="retelling-history-list">
              {retellingHistory.map((record) => (
                <RetellingHistoryItem key={record.id} record={record} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Retelling 历史记录项 */
function RetellingHistoryItem({ record }: { record: RetellingRecord }) {
  const [expanded, setExpanded] = useState(false);
  const { analysis, createdAt, recordingDataUrl } = record;
  const coverageScore = analysis.coverageScore;
  const scoreClass = coverageScore >= 80 ? 'good' : coverageScore >= 60 ? 'fair' : 'weak';

  return (
    <div className="retelling-history-item">
      <button
        type="button"
        className="history-item-header"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="history-date">
          {new Date(createdAt).toLocaleDateString('zh-CN', {
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
        <span className={`history-score ${scoreClass}`}>
          {coverageScore}%
        </span>
        <span className="history-expand">{expanded ? '▼' : '▶'}</span>
      </button>

      {expanded && (
        <div className="history-item-detail">
          {/* 录音回放 */}
          {recordingDataUrl && (
            <div className="history-audio">
              <span className="detail-label">录音:</span>
              <audio controls src={recordingDataUrl} style={{ width: '100%', height: '32px' }} />
            </div>
          )}

          {/* 关键点覆盖 */}
          {analysis.keyPoints.length > 0 && (
            <div className="history-keypoints">
              <span className="detail-label">关键点覆盖:</span>
              <div className="keypoints-list">
                {analysis.keyPoints.map((kp, idx) => (
                  <div key={idx} className={`keypoint ${kp.coverage}`}>
                    <span className="kp-status">
                      {kp.coverage === 'full' ? '✓' : kp.coverage === 'partial' ? '◐' : '✗'}
                    </span>
                    <span className="kp-text">{kp.originalPoint}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 整体反馈 */}
          {analysis.overallFeedback && (
            <div className="history-feedback">
              <span className="detail-label">AI 反馈:</span>
              <p className="feedback-text">{analysis.overallFeedback}</p>
            </div>
          )}

          {/* 词汇建议 */}
          {analysis.suggestedVocab.length > 0 && (
            <div className="history-vocab">
              <span className="detail-label">词汇建议:</span>
              <div className="vocab-list">
                {analysis.suggestedVocab.slice(0, 3).map((v, idx) => (
                  <div key={idx} className="vocab-item">
                    <span className="vocab-word">{v.word}</span>
                    <span className="vocab-suggestion">→ {v.suggestion}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
